import { route } from '@betterment/js-runtime';

import brochureUrl from './brochureUrl';

import { AppContextType } from '../apps/MultidocUploadFlowApp';
import { Account, Card, Invitation } from '../Checking/data';

// Pangea routes are defined at the bottom of this file
export const urlBuilder = {
  getBrochureUrl() {
    return brochureUrl('/');
  },
  getTaxWithholdingNotice() {
    return brochureUrl('/legal/tax-withholding-notice');
  },
  getIraSpecialConsiderations() {
    return brochureUrl('/help/inherited-ira-special-considerations');
  },
  getCryptoAccountBalanceUrl() {
    return brochureUrl('/legal/portfolio-minimum');
  },
  getFlexiblePortfolioDisclosures() {
    return brochureUrl('/legal/flexible-portfolio-disclosures');
  },
  getPortfolioDisclosures() {
    return brochureUrl(`/legal/product-and-services`);
  },
  getPortfolioStrategyHistoricalPerformance() {
    return brochureUrl(`/performance`);
  },
  getPricingUrl() {
    return brochureUrl('/pricing');
  },
  getTradAndRoth401kEmployeeResourceUrl() {
    return brochureUrl('/employees/resources/traditional-and-roth-401ks');
  },
  getUploadMultidocData(appContext: AppContextType) {
    if (appContext === 'investing') return '/api/v2/multidoc_uploads';
    return '/api/v2/checking_multidoc_uploads';
  },
  getUploadMultidocSuccessPage() {
    return '/app/multidoc_uploads/success';
  },
  getCheckingMultidocUploadPage() {
    return '/app/checking_multidoc_uploads';
  },
  getInvestingMultidocUploadPage() {
    return '/app/multidoc_uploads';
  },
  getLegalNameChangeFlowPage() {
    return '/app/legal_name_change_flow';
  },
  getPreferredNameChangePage() {
    return '/app/preferred_name_change_flow/edit';
  },
  getReferAFriend: route('', ['app', 'personal_referral_invites']),
  getRollovers: route('', ['app', 'rollovers', 'new']),
  getEmployeesResourceCenter(article: string) {
    return brochureUrl(`/employees/resources/${article}`);
  },
  getHelpCenter(article?: string) {
    return article ? brochureUrl(`/help/${article}`) : brochureUrl(`/help`);
  },
  getLegalDisclosures() {
    return brochureUrl('/legal');
  },
  getLegalDisclosure(disclosure: string) {
    return brochureUrl(`/legal/${disclosure}`);
  },
  getTcpDisclosure() {
    return brochureUrl('/legal/tax-coordination');
  },
  getTaxLocationSetup() {
    return '/app/tax_location_setup/introduction';
  },
  getPrivacyPolicy() {
    return brochureUrl('/legal/privacy-policy');
  },
  getIosAppStoreUrl() {
    return 'https://apps.apple.com/us/app/betterment-investing-saving/id393156562';
  },
  getAndroidGooglePlayStoreUrl() {
    return 'https://play.google.com/store/apps/details?id=com.betterment&hl=en_US&gl=US';
  },
  getSendSafelyLegalNameChangeUrl() {
    return 'https://sendsafely.betterment.com/dropzone/legal_name_changes';
  },
  getGoogleFinanceUrl() {
    return 'https://www.google.com/finance';
  },
  getEBSALookAt401kPlanFeesUrl() {
    return 'https://www.dol.gov/sites/dolgov/files/EBSA/about-ebsa/our-activities/resource-center/publications/a-look-at-401k-plan-fees.pdf';
  },
  getLimitedScopeAgreement() {
    return brochureUrl('/legal/limitedscopeagreement');
  },
  getCustomerAgreement() {
    return brochureUrl('/customeragreement');
  },
  getAdv() {
    return brochureUrl('/adv');
  },
  getCashReserveTermsAndConditions() {
    return brochureUrl('/legal/cash-reserve');
  },
  getLegalFormCrs() {
    return brochureUrl('/legal/form-crs');
  },
  getLegalFormAdv() {
    return brochureUrl('/legal/form-adv-part-2');
  },
  getIraRecharacterization() {
    return `/app/ira_recharacterizations`;
  },
  getIraRecharacterizationEarningsAmountCents() {
    return '/api/v2/ira_recharacterization/earnings';
  },
  postIraRecharacterizationFlowSubmission() {
    return '/api/v2/ira_recharacterization/creation';
  },
  getContactPage() {
    return brochureUrl('/contact');
  },
  getAssetLocationMethodologyPage() {
    return brochureUrl('/resources/asset-location-methodology');
  },
  getDisqualifyingEventsPage() {
    return brochureUrl('/legal/marketing-rule-disqualifying-events');
  },
  apiV2SubAccountIraContributionLimits: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'ira_contribution_limits',
  ]),
  getUser() {
    return '/api/v2/current_user';
  },
  getBankDocuments() {
    return '/api/v2/checking/bank_documents';
  },
  getCustomer() {
    return '/api/v2/checking/customer';
  },
  getSudoCheck() {
    return '/api/v2/sudo_check';
  },
  getDepositAvailabilityEstimate() {
    return '/api/v2/checking/deposit_availability_date_estimate';
  },
  getWithdrawalAvailabilityEstimate() {
    return '/api/v2/checking/withdrawal_availability_date_estimate';
  },
  getTransactions(accountId: Account['id']) {
    return `/api/v2/checking/accounts/${accountId}/transactions`;
  },
  getDetailedTransactionExport(accountId: Account['id'], exportId: string) {
    return `/api/v2/checking/accounts/${accountId}/detailed_transaction_exports/${exportId}`;
  },
  getDepositSetup() {
    return '/app/deposit_setup';
  },
  getSettings() {
    return '/app/settings';
  },
  getDocuments() {
    return '/app/documents';
  },
  getOtherDocuments() {
    return '/app/other_documents';
  },
  createTransactionCancellation(transactionId: number | string) {
    return `/api/v2/checking/transactions/${transactionId}/cancellation`;
  },
  createDetailedTransactionCancellation(transactionId: number | string) {
    return `/api/v2/checking/detailed_transactions/${transactionId}/cancellation`;
  },
  createDetailedTransactionExport(accountId: number) {
    return `/api/v2/checking/accounts/${accountId}/detailed_transaction_exports`;
  },
  getReferrals() {
    return '/api/v2/refer_a_friend/referrals';
  },
  postPersonalReferralInvites() {
    return '/api/v2/refer_a_friend/personal_referral_invites';
  },
  patchPersonalReferralInvites(personalReferralInviteId: string) {
    return `/api/v2/refer_a_friend/personal_referral_invites/${personalReferralInviteId}`;
  },
  getOfferTerms(offerName: string) {
    return `/app/offers/${offerName}`;
  },
  getAppCheckingDoshWebToken() {
    return '/app/checking_dosh_web_token/new';
  },
  appPerformance: route('', ['app', 'performance']),
  appTlhSetupSuitabilityIntroduction: route('', ['app', 'tlh_setup', 'tlh_suitability', 'introduction']),
  appGoalCreationIntents: route('', ['app', 'goal_creation_intents', { param: 'goalCreationIntentId' }]),
  postCancelJointCheckingInvitation(jointAccountInvitationId: Invitation['invitationId']) {
    return `/api/v2/checking/invitations/${jointAccountInvitationId}/cancellation`;
  },
  postActivateCard(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/verification`;
  },
  postAdvisorInvitesAcceptances(inviteId: string) {
    return `/api/v2/advisor_invites/${inviteId}/acceptances`;
  },
  postValidateAddress() {
    return '/api/v2/address_validation';
  },
  postPinReset(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/pin_reset`;
  },
  postInitialPinSetup(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/initial_pin_setup`;
  },
  postCardLock(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/lock`;
  },
  postCardUnlock(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/unlock`;
  },
  postDeposit(accountId: Account['id']) {
    return `/api/v2/checking/accounts/${accountId}/deposits`;
  },
  postWithdrawal(accountId: Account['id']) {
    return `/api/v2/checking/accounts/${accountId}/withdrawals`;
  },
  postCheckingCustomerCreation: route('/api/v2', ['checking', 'customer_creation']),
  createEditAccessRequest(invitationId: Invitation['invitationId']) {
    return `/api/v2/checking/invitations/${invitationId}/acceptance`;
  },
  createDeclineInvitationRequest(invitationId: Invitation['invitationId']) {
    return `/api/v2/checking/invitations/${invitationId}/declination`;
  },
  createProspectSecondaryJointInvitationRequest() {
    return '/api/v2/checking/prospect_secondary_joint_invitation_requests';
  },
  apiV2CheckingJointInvitationRequestCompletion: route('/api/v2', [
    'checking',
    'joint_invitation_requests',
    { param: 'requestId' },
    'completion',
  ]),
  apiV2CheckingJointInvitationRequest: route('/api/v2', ['checking', 'joint_invitation_requests']),
  apiV2CheckingAccountCreationRequests: route('/api/v2', ['checking', 'account_creation_requests']),
  apiV2CheckingAccountCreationRequestsCompletion: route('/api/v2', [
    'checking',
    'account_creation_requests',
    { param: 'requestId' },
    'completion',
  ]),
  apiV2CheckingDirectDepositConfig: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'direct_deposit_config',
  ]),
  apiCheckingAccountDirectDepositPdf: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'direct_deposit_form',
  ]),
  postSwitchSpendingAccount(cardId: Card['id']) {
    return `/api/v2/checking/cards/${cardId}/spending_account`;
  },
  appExternalAccountAssociation: route('', [
    'app',
    'goals',
    { param: 'goalId' },
    'external_account_association',
    'new',
  ]),
  getGoalOverview(goalId: string) {
    return `/app/goals/${goalId}/overview`;
  },
  getSummaryTransitionaryGoals() {
    return '/api/v2/summary/transitionary_goals';
  },
  getGoalImage(goalId: number) {
    return `/api/v2/goals/${goalId}/image.png`;
  },
  apiV2Goal: route('/api/v2', ['goals', { param: 'goalId' }]),
  appGoalRecommendation: route('', ['app', 'goal_recommendation']),
  showGoal(goalId: number) {
    return `/app/goals/${goalId}/overview`;
  },
  showHowToImproveGoal(goalId: number) {
    return `/app/goals/${goalId}/pricing/how_to_improve`;
  },
  appGoalOverview: route('app', ['goals', { param: 'goalId' }, 'overview']),
  appLegacyGoalOverview: route('', ['app', 'goals', { param: 'goalId' }, 'overview']),
  getGoalSettings: route('', ['app', 'goals', { param: 'goalId' }, 'settings']),
  showEditGoalProfileFlow: route('', ['app', 'goals', { param: 'goalId' }, 'edit_profile']),
  apiV2EditGoalProfile: route('/api/v2', ['goals', { param: 'goalId' }, 'edit_profile']),
  apiV2ValidateGoalName: route('/api/v2', ['goals', { param: 'goalId' }, 'name_validation']),
  addGoal: route('', ['app', 'add_goal']),
  newGoalAllocationChangeFlow: route('', ['app', 'goal_allocation_change_flows', 'new']),
  showReceivedInvitation(invitationId: Invitation['invitationId']) {
    return `/api/v2/checking/received_invitations/${invitationId}`;
  },
  showSubAccountTransferSetup: route('', ['app', 'sub_accounts', { param: 'subAccountId' }, 'transfer_setup']),
  showTransferSetup() {
    return '/app/transfer_setup';
  },
  createTransferDestinationAccountsList() {
    return '/api/v2/transfer_destination_accounts';
  },
  apiV2TransferSourceAccounts: route('/api/v2', ['transfer_source_accounts']),
  createSubAccountTransfer(subAccountId: number) {
    return `/api/v2/sub_accounts/${subAccountId}/transfers`;
  },
  createCheckingAccountTransfer() {
    return `/api/v2/checking/transfers`;
  },
  createNextStepPaths() {
    return `/api/v2/transfer_next_step_path`;
  },
  createSubAccountTransferTaxImpact(subAccountId: number) {
    return `/api/v2/sub_accounts/${subAccountId}/transfer_tax_impacts`;
  },
  getPendingTransfers() {
    return '/app/transfers/pending_transfers';
  },
  postSkipTask() {
    return '/app/user_task_skip';
  },
  getCheckingMultidocUploadsNew() {
    return '/app/checking_multidoc_uploads/';
  },
  getCheckingMultidocUploadsSuccess() {
    return '/app/multidoc_uploads/success';
  },
  getLegalAccounts() {
    return '/app/legal_accounts';
  },
  apiV2RolloverReminderDismissals: route('/api/v2', ['rollovers', { param: 'rolloverId' }, 'reminder_dismissals']),
  appRolloverDocument: route('/app', ['rollovers', { param: 'rolloverId' }, 'document']),
  appAddGoalFlowsNew: route('', ['app', 'add_goal_flows', 'new']),
  sidebarAddGoalFlowsNew: route('/app', ['add_goal_flows', 'new']),
  appWithdrawalSetup: route('', ['app', 'withdrawal_setup']),
  getWithdrawalSourceAccounts() {
    return '/api/v2/withdrawal_source_accounts';
  },
  getDepositDestinationAccounts() {
    return '/api/v2/deposit_destination_accounts';
  },
  createWithdrawalBankAccountsList() {
    return '/api/v2/withdrawal_bank_accounts';
  },
  createDepositBankAccountsList() {
    return '/api/v2/deposit_bank_accounts';
  },
  apiV2AutomaticWithdrawalConfig: route('/api/v2', [
    'automatic_withdrawal_configs',
    { param: 'automaticWithdrawalConfigId' },
  ]),
  apiV2AutomaticDepositConfig: route('/api/v2', ['automatic_deposit_configs', { param: 'automaticDepositConfigId' }]),
  apiV2ManualBankAccountVerifications: route('/api/v2', ['manual_bank_account_verifications']),
  getWithdrawalRecommendation() {
    return '/api/v2/withdrawal_recommendations';
  },
  editFinancialInformation() {
    return `/app/financial_profile/edit`;
  },
  createWithdrawalNextStepPaths() {
    return `/api/v2/withdrawal_next_step_path`;
  },
  updatePreferredName: route('', ['app', 'preferred_name_change_flow']),
  appCharitableGiftStart: route('', ['app', 'charitable_gift_start']),
  appCryptoOverview: route('', ['app', 'crypto_overview']),
  apiV2CheckingLinkableFundingAccounts: route('/api/v2', ['checking', 'linkable_funding_accounts']),
  apiV2CheckingExternalAccounts: route('/api/v2', ['checking', 'external_accounts']),
  apiV2LegalAccountsLinkableFundingAccounts: route('/api/v2', [
    'legal_accounts',
    { param: 'legalAccountId' },
    'linkable_funding_accounts',
  ]),
  apiV2LegalAccountsExternalAccountBankAccounts: route('/api/v2', [
    'legal_accounts',
    { param: 'legalAccountId' },
    'external_account_bank_accounts',
  ]),
  apiV2LegalAccountsCheckingBankAccounts: route('/api/v2', [
    'legal_accounts',
    { param: 'legalAccountId' },
    'checking_bank_accounts',
  ]),
  apiV2RothLimit: route('/api/v2', ['roth_limits', { param: 'taxYear' }]),
  apiV2SubaccountsWithdrawalTaxImpacts: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'withdrawal_tax_impacts',
  ]),
  apiV2SubAccountsWithdrawals: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'withdrawals']),
  apiV2BankAccountCreationFlow: route('/api/v2', ['bank_account_creation_flows']),
  newManualBankAccount: route('', [
    'app',
    'legal_accounts',
    { param: 'legalAccountId' },
    'manual_bank_accounts',
    'new',
  ]),
  newAppFourOhOneKInternalRolloverConfirmation: route('', [
    'app',
    'four_oh_one_k_internal_rollover_confirmation',
    'new',
  ]),
  new401kDistributionTypeSelection: route('', [
    'app',
    'sub_accounts',
    { param: 'subAccountId' },
    'four_oh_one_k_distribution_type_selections',
    'new',
  ]),
  getSubAccountWithdrawalDistributionTypes: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'withdrawal_distribution_types',
  ]),
  apiV2SubAccountsIraContributions: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'ira_contributions']),
  apiV2SubAccountExcessContributionCalculations: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'excess_contribution_calculations',
  ]),
  apiV2SubAccountsWithdrawalCalculations: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'withdrawal_calculations',
  ]),
  appAutomaticDepositConfig: route('app', ['automatic_deposit_configs', { param: 'automaticDepositConfigId' }]),
  appAutomaticDepositConfigEdit: route('app', [
    'automatic_deposit_configs',
    { param: 'automaticDepositConfigId' },
    'edit',
  ]),
  appAutomaticWithdrawalConfig: route('app', [
    'automatic_withdrawal_configs',
    { param: 'automaticWithdrawalConfigId' },
  ]),
  appAutomaticWithdrawalConfigEdit: route('app', [
    'automatic_withdrawal_configs',
    { param: 'automaticWithdrawalConfigId' },
    'edit',
  ]),
  appRecurringTransferConfig: route('app', ['recurring_transfer_configs', { param: 'recurringTransferConfigId' }]),
  appRecurringTransferConfigEdit: route('app', [
    'recurring_transfer_configs',
    { param: 'recurringTransferConfigId' },
    'edit',
  ]),
  apiV2RecurringTransferConfig: route('/api/v2', [
    'recurring_transfer_configs',
    { param: 'recurringTransferConfigId' },
  ]),
  appDepositSetup: route('', ['app', 'deposit_setup']),
  appDepositSetupDestination: route('', ['app', 'deposit_setup', 'destination']),

  appConvertFundsToRoth: route('', ['app', 'sub_accounts', { param: 'subAccountId' }, 'ira_conversion_flows', 'new']),

  appTransferredUserOnboarding: route('', ['app', 'transferred_user_onboardings', { param: 'invitationToken' }]),
  apiV2TransferredUserOnboardingsIdentityVerification: route('/api/v2', [
    'transferred_user_onboardings',
    { param: 'invitationToken' },
    'identity_verification',
  ]),
  apiV2TransferredUserOnboardingsAddressValidation: route('/api/v2', [
    'transferred_user_onboardings',
    { param: 'invitationToken' },
    'address_validation',
  ]),
  apiV2TransferredUserOnboardingsCompletion: route('/api/v2', [
    'transferred_user_onboardings',
    { param: 'invitationToken' },
    'completion',
  ]),
  apiV2SubAccountWireTransfersNew: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'wire_transfers',
    'new',
  ]),
  apiV2SubAccountWireTransfers: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'wire_transfers']),
  apiV2DepositDestinationAccounts: route('/api/v2', ['deposit_destination_accounts']),
  apiV2DepositBankAccountsList: route('/api/v2', ['deposit_bank_accounts']),
  apiV2DepositNextStepPath: route('/api/v2', ['deposit_next_step_path']),
  apiV2SubAccountsDeposits: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'deposits']),
  apiV2EmailSubmission: route('/api/v2', ['email_submission']),
  apiV2OnboardingUsers: route('/api/v2', ['onboarding', 'users']),
  apiV2OnboardingProfileSetup: route('/api/v2', ['onboarding', 'profile_setups']),
  apiV2OnboardingDraftAccounts: route('/api/v2', ['onboarding', 'draft_accounts']),
  apiV2OnboardingAccounts: route('/api/v2', ['onboarding', 'accounts']),
  apiV2ValidateAddress: route('/api/v2', ['address_validation']),
  apiV2ValidatePhoneNumber: route('/api/v2', ['phone_number_validation']),
  apiV2User: route('/api/v2', ['users']),
  apiV2ProfileSetupFlowCompletions: route('/api/v2', ['users', 'profile_setup_flow_completion']),
  apiV2AdviceInitiationCalendlyCall: route('/api/v2', ['advice_initiation', 'calendly_call']),
  apiV2AdviceInitiationIntendedTransferAmount: route('/api/v2', ['advice_initiation', 'intended_transfer_amount']),
  apiV2AdviceInitiationPremiumUpgrade: route('/api/v2', ['advice_initiation', 'premium_upgrade']),
  apiV2SubAccountCashMatchFeeSearch: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'cash_match_fee_search',
  ]),
  appImpersonationBlocker: route('', ['app', 'impersonation_blocker']),
  appLogin: route('', ['app', 'login']),
  appProfileSetupFlow: route('', ['app', 'profile_setup_flow']),
  appDepositRequestMemo: route('', ['app', 'deposit_requests', { param: 'depositRequestId' }, 'memo', 'new']),
  appWithdrawalRequestMemo: route('', ['app', 'withdrawals', { param: 'withdrawalRequestId' }, 'memo', 'new']),
  appExcessContributionRemovalRequestMemo: route('', [
    'app',
    'excess_contribution_removals',
    { param: 'excessContributionRemovalRequestId' },
    'memo',
    'new',
  ]),
  appDistributionRequestMemo: route('', ['app', 'distributions', { param: 'distributionRequestId' }, 'memo', 'new']),
  apiV2IrsStateWithholdingRules: route('/api/v2', ['ira_state_withholding_rules']),
  apiV2IrsStateWithholdingValidation: route('/api/v2', ['ira_state_withholding_validation']),
  appPlaidLink: route('', ['app', 'plaid_link']),
  apiV2PlaidLinkTokens: route('/api/v2', ['plaid', 'link_tokens']),
  apiV2PlaidUpdateModeLinkTokens: route('/api/v2', ['plaid', 'update_mode_link_tokens']),
  apiV2PlaidItems: route('/api/v2', ['plaid', 'items']),
  apiV2PlaidItem: route('/api/v2', ['plaid', 'items', { param: 'plaidItemId' }]),
  apiV2PlaidItemRefreshes: route('/api/v2', ['plaid', 'items', { param: 'plaidItemId' }, 'refreshes']),
  apiV2PlaidItemExternalAccounts: route('/api/v2', ['plaid', 'items', { param: 'plaidItemId' }, 'external_accounts']),
  apiV2DocVerification: route('/api/v2', ['document_verification']),
  getAppDocumentVerifications: route('', ['app', 'document_verifications']),
  appFixConnection: route('', ['app', 'plaid_items', { param: 'plaidItemId' }, 'fix_connection']),
  appBankAccountSetup: route('', ['app', 'legal_accounts', { param: 'legalAccountId' }, 'bank_account_setup']),
  appExternalAccountSetup: route('', ['app', 'external_account_setup']),
  appExternalAccounts: route('', ['app', 'external_accounts']),
  appExternalAccountsNew: route('', ['app', 'external_accounts', 'new']),
  appExternalAccountsRolloverStart: route('', [
    'app',
    'external_accounts',
    { param: 'externalAccountId' },
    'rollover_start',
  ]),
  appExternalAccountsHoldings: route('', ['app', 'external_accounts', { param: 'externalAccountId' }, 'holdings']),
  appExternalTaxableAccountTransferFlowsNew: route('', ['app', 'external_taxable_account_transfer_flows', 'new']),
  apiV2CheckingBankLinkException: route('/api/v2', ['checking', 'bank_link_exceptions']),
  getCheckingAccountTasks: route('/api/v2', ['checking', 'accounts', { param: 'accountId' }, 'tasks']),
  getCheckingSignupData: route('/api/v2', ['checking', 'signup_data']),
  apiV2CheckingStateAgeLimitEligibility: route('/api/v2', ['checking', 'state_age_limit_eligibility']),
  apiV2CheckingDetailedTransactions: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'detailed_transactions',
  ]),
  apiV2CheckingDetailedTransaction: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'detailed_transactions',
    { param: 'transactionId' },
  ]),
  apiV2AddGoalAvailableAllocations: route('/api/v2', ['add_goal_flows', { param: 'flowId' }, 'available_allocations']),
  apiV2AddGoalAllocationSelection: route('/api/v2', ['add_goal_flows', { param: 'flowId' }, 'allocation_selection']),
  apiV2InAppContentBlogPosts: route('/api/v2', ['in_app_content', 'blog_posts']),
  appEstimatedStudentLoanPaymentFlow: route('app', ['employment', 'estimated_student_loan_payments']),
  appEndOfYearAttestationApp: route('app', ['employment', 'attested_student_loan_payments']),
  appFiveTwoNineSignup: route('app', ['five_two_nine_signup']),
  appFiveTwoNinePortfolioSelection: route('app', ['five_two_nine_portfolio_selection', { param: 'planIntentId' }]),
  appFiveTwoNineAccountDetails: route('app', ['five_two_nine_account_details', { param: 'planIntentId' }]),
  appFiveTwoNineConnectUnite: route('app', ['five_two_nine_connect_unite']),
  appFiveTwoNineOhioCDPurchaseFlow: route('app', [
    'goals',
    { param: 'goalId' },
    'five_two_nine',
    'ohio_cd_purchases_completion',
    'flow',
  ]),
  appFiveTwoNineOhioCDPurchaseSuccess: route('app', [
    'goals',
    { param: 'goalId' },
    'five_two_nine',
    'ohio_cd_purchases_completion',
    'success',
  ]),
  apiV2ExternallyAdvisedPlanAllocations: route('/api/v2', [
    'goals',
    { param: 'goalId' },
    'edit_portfolio_flows',
    'externally_advised_plan_allocations',
  ]),

  apiV2EducationSavingsFiveTwoNinePlanIntents: route('/api/v2', ['education_savings', 'five_two_nine', 'plan_intents']),
  apiV2EducationSavingsFiveTwoNinePortfolioIntents: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'portfolio_intents',
  ]),
  apiV2EducationSavingsFiveTwoNinePlanSelectionAdvice: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'plan_selection_advice',
  ]),
  apiV2EducationSavingsFiveTwoNineAscensusSsoConnectionIntents: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_sso_connection_intents',
  ]),
  apiV2EducationSavingsFiveTwoNineExternalAccountConnectionIntents: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'external_account_connection_intents',
  ]),
  apiV2EducationSavingsFiveTwoNinePortfolioSelectionAdvice: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'portfolio_selection_advice',
  ]),
  apiV2EducationSavingsFiveTwoNineEnrollmentDetailsValidation: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'enrollment_details_validations',
  ]),
  apiV2EducationSavingsFiveTwoNineAscensusEnrollments: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_enrollments',
  ]),
  apiV2EmploymentEmployeeEstimatedSlmPayment: route('/api/v2', ['employment', 'employee_estimated_slm_payments']),
  apiV2Annual401kDeferralProjection: route('/api/v2', ['annual_401k_deferral_projection']),
  appAscensusSsoInitiations: route('', ['app', 'ascensus', 'sso_initiation']),
  apiV2EducationSavingsFiveTwoNineAscensusAccounts: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_accounts',
  ]),
  apiV2EducationSavingsFiveTwoNineAscensusPlanConnections: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_plan_connections',
  ]),
  appPayrollAssumptions: route('', ['app', 'employees', { param: 'employeeId' }, 'payroll_assumptions', 'new']),
  appDeferralSetting: route('', ['app', 'legal_accounts', { param: 'legalAccountId' }, 'deferral_setting', 'edit']),
  apiV2GoalsProjectedBalance: route('/api/v2', ['goals', { param: 'goalId' }, 'projected_balance']),
  apiV2GoalsRetirementProjections: route('/api/v2', ['goals', { param: 'goalId' }, 'retirement_projections']),
  appPayrollDeductionSetup: route('app', ['payroll_deduction_setup']),
  apiV2PayrollDeductionChanges: route('/api/v2', ['payroll_deduction_changes']),
  apiV2GlobalNotices: route('', ['api/v2', 'global_notices']),
  apiV2MessageDismissals: route('', ['api/v2', 'message_dismissals', { param: 'name' }]),
  appAdviceInitiationFlow: route('', ['app', 'advice_initiation']),
  apiV2MarketingMessages: route('', ['api/v2', 'marketing_messages']),
  apiV2MarketingMessagesGroups: route('', ['api/v2', 'marketing_messages_groups']),
  apiGraphQL: route('', ['api/graphql/web']),
  appRewards: route('/app/rewards', []),
  appOnboardingFlow: route('', ['app', 'onboarding_flow']),
  appProfile: route('', ['app', 'profile']),
  appEarnedRewards: route('', ['app', 'earned_rewards']),
  markFirmFormCRSDelivered: route('/api/v2', ['firm_form_crs_notification_deliveries']),

  appGoalsEditAllocation: route('', ['app', 'goal_allocation_change_flows', 'new']),
  appGoalsEditPurpose: route('', ['app', 'edit_goal_details_flows', 'new']), // query params: goal_id, skip_to_step, return_to
  appGoalsEditPortfolioStrategy: route('', [
    'app',
    'goals',
    { param: 'goalId' },
    'edit_portfolio',
    'portfolio_strategy_selection',
    'new',
  ]),
  appGoalsEditProfileName: route('', ['app', 'goals', { param: 'goalId' }, 'edit_profile', 'goal_name']),
  appGoalsEditProfileImage: route('', ['app', 'goals', { param: 'goalId' }, 'edit_profile', 'goal_image']),

  // Pangea routes
  // Pangea routes should take in '/app' as the basePath to work with react router and for consistency
  // Name Pangea routes `appNameOfPath` for consistency

  appHome: route('/app', []),
  getHome() {
    return '/app';
  },

  appDirectTradingAccount: route('/app', ['direct_trading_accounts', { param: 'directTradingAccountId' }]),
  appActivity: route('/app', ['activity']),
  appAccount: route('/app', ['account', { param: 'accountId' }]),
  appAccountForecaster: route('app', ['account', { param: 'accountId' }, 'forecaster']),
  appPurpose: route('/app', ['purpose', { param: 'purposeId' }]),
  appPurposeForecaster: route('app', ['purpose', { param: 'purposeId' }, 'forecaster']),
  appExternallyAdvisedPlan: route('/app', ['goals', { param: 'goalId' }, 'edit_portfolio', 'externally_advised_plan']),

  emailVerificationCheckpoint: route('/app', ['email_verification_checkpoint']),

  appIraDetails: route('/app', ['ira_details']),
  appFourOhOneKDetails: route('/app', ['four_oh_one_k_details']),
  appFourOhOneKSelect: route('/app', ['four_oh_one_k_select']),

  appPersonalInfoEdit: route('/app', ['personal_info', 'edit']),
  appPersonalReferralInvites: route('/app', ['personal_referral_invites']),
  appFiveTwoNineOhioCDPurchase: route('/app', [
    'goals',
    { param: 'goalId' },
    'five_two_nine',
    'ohio_cd_purchases_completion',
  ]),

  // Checking routes
  getAppChecking: route('/app', ['checking']),
  getAppCheckingSummary: route('/app', ['checking', 'accounts', { param: 'accountId' }]),
  appCheckingSignups: route('/app', ['checking', 'signups']),
  appCheckingExternalAccountSetup: route('/app', ['checking', 'external_account_setup']),
  getAppCheckingProspectSignup: route('/app', ['checking', 'prospect_signup']),
  getAppCheckingAbout: route('/app', ['checking', 'about']),
  getAppCheckingAboutCashback: route('/app', ['checking', 'about_cash_back']),
  getAppCheckingAboutNoFeeChecking: route('/app', ['checking', 'about_no_fee_checking']),
  getAppCheckingAccountCreation: route('/app', ['checking', 'account_creation']),
  getAppCheckingAccountTransactions: route('/app', ['checking', 'accounts', { param: 'accountId' }, 'transactions']),
  getAppCheckingAccountDirectDeposit: route('/app', ['checking', 'accounts', { param: 'accountId' }, 'direct_deposit']),
  getAppCheckingAccountDepositSetup: route('/app', ['checking', 'accounts', { param: 'accountId' }, 'deposit_setup']),
  getAppCheckingTransactionsExport: route('/app', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'transactions_export',
  ]),
  getCheckingWithdrawalSetup: route('/app', ['checking', 'accounts', { param: 'accountId' }, 'withdrawal_setup']),
  getAppCheckingCardSetup: route('/app', ['checking', 'card_setup']),
  getAppCheckingPinChange: route('/app', ['checking', 'pin_change']),
  getCheckingFundingOptions: route('/app', ['checking', 'accounts', { param: 'accountId' }, 'funding_options']),
  getAppCheckingJointAccounts: route('/app', ['checking', 'joint_accounts']),
  appCheckingJointAccountsInvitation: route('/app', [
    'checking',
    'joint_accounts_invitations',
    { param: 'jointAccountInvitationId' },
  ]),
  getAppCheckingJointInvitationAcceptanceSuccess: route('/app', [
    'checking',
    'joint_accounts_invitations',
    { param: 'jointAccountInvitationId' },
    'success',
  ]),
};
